import { StatusTag } from "@/views/components/StatusTag";
import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Card, Input, Select, Space, Tabs, Typography, message } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { Customer } from "@zhihaoscm/entity";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { customerService } from "@/api/services/customerService";
import { getInfoFromCustomer } from "@/utils/getInfoFromCustomer";
import { YES_OR_NO } from "@/meta/common";
import { useUserStore } from "@/views/stores";

export interface CapacityCertificationQuery {
    page: number;
    size: number;
    verifier: "2" | "1";
    keyword?: string;
    carrier?: number | string;
    state?: number;
}

export interface CapacityCertificationRow {
    ship?: {
        id?: string;
        cnname?: string;
        mobile?: string;
        qhyAuditStatus?: number;
        hmgCheckState?: number;
        qhyAuditTime?: string;
        hmgCheckTime?: string;
        qhyAuditRemark?: string;
        hmgCheckRemark?: string;
    };
    customer?: { realName?: string; institutionName?: string };
}

export interface CapacityCertificationPageProps {
    loadCertification?: (
        query: CapacityCertificationQuery
    ) => Promise<{ list?: CapacityCertificationRow[]; total?: number }>;
    refreshCertification?: (id: string, verifier: "2" | "1") => Promise<unknown>;
    reAuthCertification?: (id: string, verifier: "2" | "1") => Promise<unknown>;
    canManage?: boolean;
}

/** 使用 Vue 运力认证列表接口，保留签好运/黄码港 verifier 参数。 */
export async function loadCapacityCertification(query: CapacityCertificationQuery) {
    const response = await shippingRequestHttp.service.get("/ship/auth/paging", { params: query });
    return response.data;
}

/** 根据认证平台调用 Vue 对应的刷新接口。 */
export async function refreshCapacityCertification(id: string, verifier: "2" | "1") {
    const response = await shippingRequestHttp.service.get(
        verifier === "2" ? `/ship/refresh/${id}` : `/ship/hmg-refresh/${id}`
    );
    return response.data;
}

/** 根据认证平台调用 Vue 对应的重新认证接口。 */
export async function reAuthCapacityCertification(id: string, verifier: "2" | "1") {
    const response = await shippingRequestHttp.service.put(
        verifier === "2" ? `/ship/auth/${id}` : `/ship/hmg-check/${id}`
    );
    return response.data;
}

const qianhaoyunStates = [
    { value: 11, label: "认证中" },
    { value: 12, label: "已认证" },
    { value: 13, label: "认证失败" },
    { value: 14, label: "已过期" }
];
const huangmagangStates = [
    { value: 2, label: "待审核" },
    { value: 3, label: "已通过" },
    { value: 4, label: "审核失败" }
];
const dateText = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : "--");

/** 物流管理-在线运输认证列表，覆盖 Vue 两个平台的筛选、状态和操作状态机。 */
export function CapacityCertificationPage({
    loadCertification = loadCapacityCertification,
    refreshCertification = refreshCapacityCertification,
    reAuthCertification = reAuthCapacityCertification,
    canManage
}: CapacityCertificationPageProps) {
    const storeCanManage = useUserStore(state => state.hasAnyPermission(["ROLE_ONLINE_FREIGHT_W"]));
    const banButton = useUserStore(state => state.banButton);
    const mayManage = canManage ?? storeCanManage;
    const navigate = useNavigate();
    const [verifier, setVerifier] = useState<"2" | "1">("2");
    const [rows, setRows] = useState<CapacityCertificationRow[]>([]);
    const [total, setTotal] = useState(0);
    const [keyword, setKeyword] = useState("");
    const [carrier, setCarrier] = useState<number>();
    const [carrierOptions, setCarrierOptions] = useState<Array<{ value: number; label: string }>>([]);
    const [state, setState] = useState<number>();
    const [query, setQuery] = useState<CapacityCertificationQuery>({ page: 1, size: 10, verifier: "2" });
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        let disposed = false;
        void loadCertification(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage("");
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("运力认证列表加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadCertification, query]);

    // 沿用 Vue 承运商远程选择器，只查询已完成个人认证的客户并最多展示 50 条结果。
    const searchCarriers = useCallback(async (value: string) => {
        if (!value.trim()) return;
        try {
            const customers = await customerService.customerSelector({
                param: value,
                personalAuth: YES_OR_NO.YES.code
            });
            setCarrierOptions(
                customers.slice(0, 50).map((customer: Customer) => ({
                    value: Number(customer.id),
                    label: getInfoFromCustomer(customer)
                }))
            );
        } catch {
            setCarrierOptions([]);
        }
    }, []);

    const submit = () =>
        setQuery({
            page: 1,
            size: 10,
            verifier,
            ...(keyword.trim() ? { keyword: keyword.trim() } : {}),
            ...(carrier === undefined ? {} : { carrier }),
            ...(state === undefined ? {} : { state })
        });
    const stateOptions = verifier === "2" ? qianhaoyunStates : huangmagangStates;
    const rowState = useCallback(
        (row: CapacityCertificationRow) => (verifier === "2" ? row.ship?.qhyAuditStatus : row.ship?.hmgCheckState),
        [verifier]
    );
    const stateText = useCallback(
        (value?: number) => stateOptions.find(item => item.value === value)?.label || "--",
        [stateOptions]
    );
    const operate = useCallback(
        async (row: CapacityCertificationRow, type: "refresh" | "reauth") => {
            const id = row.ship?.id;
            if (!id) return;
            try {
                if (type === "refresh") await refreshCertification(id, verifier);
                else await reAuthCertification(id, verifier);
                message.success(type === "refresh" ? "刷新成功" : "重新认证已提交");
                setQuery(current => ({ ...current }));
            } catch {
                setErrorMessage(type === "refresh" ? "刷新认证失败" : "重新认证失败");
            }
        },
        [reAuthCertification, refreshCertification, verifier]
    );
    // 失败认证的“处理”沿用 Vue 跳转船舶详情并清理详情页签缓存的行为。
    const handleCertification = useCallback(
        (row: CapacityCertificationRow) => {
            const id = row.ship?.id;
            if (!id) return;
            window.localStorage.removeItem("shipInfoDetailTabs");
            navigate(`/logistics/shipManage/shipInfo/shipInfoDetail/${id}`);
        },
        [navigate]
    );

    // 用 zhihao-ui-react 的列契约承接旧 Vue ProTable 字段和操作插槽。
    const columns = useMemo<TableColumnProps<CapacityCertificationRow>[]>(
        () => [
            {
                key: "id",
                label: "MMSI",
                width: 204,
                prop: "ship.id",
                render: ({ row }) => (
                    <Link to={`/logistics/shipManage/shipInfo/shipInfoDetail/${row.ship?.id || ""}`}>
                        {row.ship?.id || "--"}
                    </Link>
                )
            },
            { key: "name", label: "船舶名称", prop: "ship.cnname", render: ({ row }) => row.ship?.cnname || "--" },
            {
                key: "customer",
                label: "承运商",
                width: 350,
                prop: "customer.realName",
                render: ({ row }) => row.customer?.realName || row.customer?.institutionName || "--"
            },
            {
                key: "mobile",
                label: "手机号",
                width: 204,
                prop: "ship.mobile",
                render: ({ row }) => row.ship?.mobile || "--"
            },
            {
                key: "state",
                label: "认证状态",
                width: 204,
                prop: "ship.qhyAuditStatus",
                render: ({ row }) => <StatusTag>{stateText(rowState(row))}</StatusTag>
            },
            {
                key: "time",
                label: "认证时间",
                prop: "ship.authenticationTime",
                render: ({ row }) => dateText(verifier === "2" ? row.ship?.qhyAuditTime : row.ship?.hmgCheckTime)
            },
            {
                key: "remark",
                label: "备注",
                width: 800,
                prop: "ship.remark",
                render: ({ row }) => (verifier === "2" ? row.ship?.qhyAuditRemark : row.ship?.hmgCheckRemark) || "--"
            },
            {
                key: "operation",
                label: "操作",
                width: 350,
                prop: "operation",
                fixed: "right",
                render: ({ row }) => {
                    const value = rowState(row);
                    return mayManage ? (
                        <Space>
                            {[11, 12, 2, 3].includes(Number(value)) ? (
                                <Button disabled={banButton} type="link" onClick={() => void operate(row, "refresh")}>
                                    刷新
                                </Button>
                            ) : null}
                            {[13, 14, 4].includes(Number(value)) ? (
                                <>
                                    <Button
                                        disabled={banButton}
                                        type="link"
                                        onClick={() => void operate(row, "reauth")}
                                    >
                                        重新认证
                                    </Button>
                                    <Button disabled={banButton} type="link" onClick={() => handleCertification(row)}>
                                        处理
                                    </Button>
                                </>
                            ) : null}
                        </Space>
                    ) : null;
                }
            }
        ],
        [banButton, handleCertification, mayManage, operate, rowState, stateText, verifier]
    );

    return (
        <section aria-label="capacity-certification-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <Tabs
                    activeKey={verifier}
                    onChange={key => {
                        const next = key as "2" | "1";
                        setVerifier(next);
                        setState(undefined);
                        setQuery({ page: 1, size: 10, verifier: next });
                    }}
                    items={[
                        { key: "2", label: "签好运" },
                        { key: "1", label: "黄码港" }
                    ]}
                />
                <Space wrap>
                    <Input
                        aria-label="认证船舶搜索"
                        placeholder="请输入船舶名称关键字或MMSI查询"
                        value={keyword}
                        onChange={event => setKeyword(event.target.value)}
                    />
                    <Select<number>
                        aria-label="承运商"
                        allowClear
                        showSearch
                        filterOption={false}
                        placeholder="请输入承运商名称"
                        value={carrier}
                        options={carrierOptions}
                        onSearch={value => void searchCarriers(value)}
                        onChange={value => setCarrier(value)}
                    />
                    <Select
                        aria-label="认证状态"
                        allowClear
                        placeholder="请选择认证状态"
                        value={state}
                        options={stateOptions}
                        onChange={setState}
                    />
                    <Button aria-label="查询" type="primary" onClick={submit}>
                        查询
                    </Button>
                </Space>
            </Card>
            <Card>
                <ZhTable<CapacityCertificationRow>
                    rowKey={row => String(row.ship?.id)}
                    data={rows}
                    columns={normalizeOperationColumns(columns, rows)}
                    pagination={false}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title level={4}>运力认证</Typography.Title>

                            {errorMessage ? <Alert role="alert" type="error" message={errorMessage} /> : null}
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                hideOnSinglePage
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({
                                        ...current,
                                        // 切换每页条数后回到第一页，普通翻页保留当前页。
                                        page: pageSize !== current.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export default CapacityCertificationPage;
